import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// COMPONENT
import Loader from '../../../../common/Loader';

// NETWORK
import useTariffRequest from '../../../../../requests/TariffRequest';

export default function TariffLineSingleView({
  tariffPlan,
  tariffLine,
  backButtonClick,
  isNew = false,
  errorHandler,
  isEditing,
  setIsEditing,
}) {
  const [edits, setEdits] = useState();
  const [types, setTypes] = useState([]);
  const [units, setUnits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [currentTariffLine, setCurrentTariffLine] = useState(tariffLine || {});

  const {
    getLineExtras,
    createNewTariffLine,
    updateTariffLine,
    getTariffLines,
    deleteTariffLine,
  } = useTariffRequest();

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);

      const extras = await getLineExtras(errorHandler);
      setTypes(extras.types || []);
      setUnits(extras.units || []);
      setCategories(extras.categories || []);
      setPeriods(extras.periods || []);
      setIsLoading(false);

      if (isNew) {
        setEdits({
          tariff_type: extras.types ? extras.types[0].code : '',
          category: extras.categories ? extras.categories[0].code : '',
          period: extras.periods ? extras.periods[0].code : '',
          unit: extras.units ? extras.units[0].code : '',
        });
      }
    };

    fetchInitialData();
  }, [errorHandler, isNew]);

  // Add effect to update currentTariffLine when tariffLine prop changes
  useEffect(() => {
    if (tariffLine) {
      setCurrentTariffLine(tariffLine);
    }
  }, [tariffLine]);

  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    const newValue = type === 'checkbox' ? checked : value;

    setEdits((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setHasError(false);
    setErrorMessage('');

    if (isNew) {
      if (!edits.tariff_name?.trim()) {
        setHasError(true);
        setErrorMessage('Requires a Line Name.');
        return false;
      }

      if (!edits.tariff_type) {
        setHasError(true);
        setErrorMessage('Requires a Tariff Type.');
        return false;
      }

      if (!edits.category) {
        setHasError(true);
        setErrorMessage('Requires a Tariff Category.');
        return false;
      }

      if (!edits.rate || isNaN(edits.rate)) {
        setHasError(true);
        setErrorMessage(
          isNaN(edits.rate)
            ? 'Rate should be a number.'
            : 'Requires a Tariff Rate.'
        );
        return false;
      }

      if (!edits.period) {
        setHasError(true);
        setErrorMessage('Requires a Tariff Period.');
        return false;
      }

      if (!edits.unit) {
        setHasError(true);
        setErrorMessage('Requires a Tariff Unit.');
        return false;
      }

      if (!edits.quantity || isNaN(edits.quantity)) {
        setHasError(true);
        setErrorMessage(
          isNaN(edits.quantity)
            ? 'Quantity should be a number.'
            : 'Requires a Tariff Quantity.'
        );
        return false;
      }

      if (!edits.batches || isNaN(edits.batches)) {
        setHasError(true);
        setErrorMessage(
          isNaN(edits.batches)
            ? 'Batches should be a number.'
            : 'Requires a Tariff Batches.'
        );
        return false;
      }

      setIsSaving(true);

      const result = await createNewTariffLine(
        tariffPlan.tariff_scheme_id,
        tariffPlan.tariff_plan_id,
        edits,
        errorHandler
      );

      if (!result.error) {
        // After successful creation, go back to plan view with refresh flag
        backButtonClick(true, true, false);
      }
    } else {
      if ('tariff_name' in edits && !edits.tariff_name?.trim()) {
        setHasError(true);
        setErrorMessage('Requires a Line Name.');
        return false;
      }

      if ('tariff_type' in edits && !edits.tariff_type) {
        setHasError(true);
        setErrorMessage('Requires a Tariff Type.');
        return false;
      }

      if ('category' in edits && !edits.category) {
        setHasError(true);
        setErrorMessage('Requires a Tariff Category.');
        return false;
      }

      if (
        ('rate' in edits && !edits.rate) ||
        (edits.rate && isNaN(edits.rate))
      ) {
        setHasError(true);
        setErrorMessage(
          isNaN(edits.rate)
            ? 'Rate should be a number.'
            : 'Requires a Tariff Rate.'
        );
        return false;
      }

      if ('period' in edits && !edits.period) {
        setHasError(true);
        setErrorMessage('Requires a Tariff Period.');
        return false;
      }

      if ('unit' in edits && !edits.unit) {
        setHasError(true);
        setErrorMessage('Requires a Tariff Unit.');
        return false;
      }

      if (
        ('quantity' in edits && !edits.quantity) ||
        (edits.quantity && isNaN(edits.quantity))
      ) {
        setHasError(true);
        setErrorMessage(
          isNaN(edits.quantity)
            ? 'Quantity should be a number.'
            : 'Requires a Tariff Quantity.'
        );
        return false;
      }

      if (
        ('batches' in edits && !edits.batches) ||
        (edits.batches && isNaN(edits.batches))
      ) {
        setHasError(true);
        setErrorMessage(
          isNaN(edits.batches)
            ? 'Batches should be a number.'
            : 'Requires a Tariff Batches.'
        );
        return false;
      }

      setIsSaving(true);

      const result = await updateTariffLine(
        tariffPlan.tariff_scheme_id,
        tariffPlan.tariff_plan_id,
        currentTariffLine.tariff_line_id,
        edits,
        errorHandler
      );

      if (!result.error) {
        // Fetch updated line data after successful save
        setIsLoading(true);
        const tariffLines = await getTariffLines(
          tariffPlan.tariff_scheme_id,
          tariffPlan.tariff_plan_id,
          errorHandler
        );

        // Find the updated line
        const updatedLine = tariffLines.find(
          (line) => line.tariff_line_id === currentTariffLine.tariff_line_id
        );

        setCurrentTariffLine(updatedLine);
        setIsLoading(false);
        setEdits({});
        setIsEditing(false);
      }
    }
    setIsSaving(false);
  };

  const handleDelete = async () => {
    setIsSaving(true);
    setShowConfirmation(false);
    setEdits({});

    await deleteTariffLine(
      tariffPlan.tariff_scheme_id,
      tariffPlan.tariff_plan_id,
      currentTariffLine.tariff_line_id,
      errorHandler
    );

    backButtonClick(true);
  };

  const handleBackClick = async (refreshFlag) => {
    if (isEditing) {
      toggleEdit();
      return;
    }

    if (refreshFlag) {
      setIsLoading(true);
      const tariffLines = await getTariffLines(
        tariffPlan.tariff_scheme_id,
        tariffPlan.tariff_plan_id,
        errorHandler
      );

      // Find the current line in the updated list
      const updatedLine = tariffLines.find(
        (line) => line.tariff_line_id === currentTariffLine.tariff_line_id
      );

      setCurrentTariffLine(updatedLine);
      setIsLoading(false);
    }

    backButtonClick(refreshFlag, true, false);
  };

  const toggleEdit = () => {
    setIsEditing((prev) => !prev);
  };

  const handleNewCancelClick = () => {
    if (isNew) {
      setIsEditing(false);
      backButtonClick(false, true);
    } else {
      toggleEdit();
    }
  };

  const generateFormGroup = (label, displayValue) => {
    return (
      <Grid size={12}>
        <Typography>
          <strong>{label}</strong> {displayValue}
        </Typography>
      </Grid>
    );
  };

  const renderForm = () => {
    // First check if we're in new/edit mode
    if (isNew || isEditing) {
      if (!tariffPlan) {
        return (
          <Box sx={{ mt: 2 }}>
            <Typography>No tariff plan selected</Typography>
          </Box>
        );
      }
      return (
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3} maxWidth={800}>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 100 }} required>
                  Name
                </InputLabel>
                <TextField
                  fullWidth
                  name="tariff_name"
                  required
                  defaultValue={!isNew ? currentTariffLine?.tariff_name : ''}
                  onChange={handleFormChange}
                  size="small"
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 100 }} required>
                  Type
                </InputLabel>
                {types.length > 0 && (
                  <Select
                    name="tariff_type"
                    defaultValue={
                      !isNew ? currentTariffLine?.tariff_type : types[0].code
                    }
                    onChange={handleFormChange}
                    required
                    fullWidth
                    size="small"
                  >
                    {types.map((type) => (
                      <MenuItem key={type.code} value={type.code}>
                        {type.meaning}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 100 }} required>
                  Category
                </InputLabel>
                {categories.length > 0 && (
                  <Select
                    name="category"
                    defaultValue={
                      !isNew ? currentTariffLine?.category : categories[0].code
                    }
                    onChange={handleFormChange}
                    required
                    fullWidth
                    size="small"
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.code} value={category.code}>
                        {category.meaning}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 100 }} required>
                  Rate
                </InputLabel>
                <TextField
                  fullWidth
                  name="rate"
                  type="text"
                  defaultValue={!isNew ? currentTariffLine?.rate : ''}
                  onChange={handleFormChange}
                  required
                  size="small"
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 100 }} required>
                  Period
                </InputLabel>
                {periods.length > 0 && (
                  <Select
                    name="period"
                    defaultValue={
                      !isNew ? currentTariffLine?.period : periods[0].code
                    }
                    onChange={handleFormChange}
                    required
                    fullWidth
                    size="small"
                  >
                    {periods.map((period) => (
                      <MenuItem key={period.code} value={period.code}>
                        {period.meaning}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 100 }} required>
                  Unit
                </InputLabel>
                {units.length > 0 && (
                  <Select
                    name="unit"
                    defaultValue={
                      !isNew ? currentTariffLine?.unit : units[0].code
                    }
                    onChange={handleFormChange}
                    required
                    fullWidth
                    size="small"
                  >
                    {units.map((unit) => (
                      <MenuItem key={unit.code} value={unit.code}>
                        {unit.meaning}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 100 }} required>
                  Quantity
                </InputLabel>
                <TextField
                  fullWidth
                  name="quantity"
                  type="text"
                  defaultValue={!isNew ? currentTariffLine?.quantity : ''}
                  onChange={handleFormChange}
                  required
                  size="small"
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 100 }} required>
                  Batches
                </InputLabel>
                <TextField
                  fullWidth
                  name="batches"
                  type="text"
                  defaultValue={!isNew ? currentTariffLine?.batches : ''}
                  onChange={handleFormChange}
                  required
                  size="small"
                />
              </Stack>
            </Grid>
          </Grid>

          {hasError && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error">{errorMessage}</Alert>
            </Box>
          )}

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => (isNew ? handleNewCancelClick() : toggleEdit())}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              type="submit"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>
      );
    }

    // Then check if we have a tariff line to display
    if (!currentTariffLine) {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography gutterBottom>No tariff line selected</Typography>
        </Box>
      );
    }

    // Finally, check if we have the required tariff plan for operations
    if (!tariffPlan) {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography gutterBottom>No tariff plan selected</Typography>
        </Box>
      );
    }

    return (
      <Box>
        <Grid container spacing={2}>
          {generateFormGroup(
            'Tariff Name:',
            currentTariffLine?.tariff_name || 'N/A'
          )}
          {generateFormGroup(
            'Type:',
            currentTariffLine?.tariff_type_name || 'N/A'
          )}
          {generateFormGroup('Rate:', currentTariffLine?.rate || 'N/A')}
          {generateFormGroup('Unit:', currentTariffLine?.unit_name || 'N/A')}
          {generateFormGroup(
            'Category:',
            currentTariffLine?.category_name || 'N/A'
          )}
          {generateFormGroup(
            'Period:',
            currentTariffLine?.period_name || 'N/A'
          )}
          {generateFormGroup('Quantity:', currentTariffLine?.quantity || 'N/A')}
          {generateFormGroup('Batches:', currentTariffLine?.batches || 'N/A')}
          {generateFormGroup(
            'Created Date:',
            currentTariffLine?.created_date
              ? new Date(currentTariffLine.created_date).toLocaleString()
              : 'N/A'
          )}
          {generateFormGroup(
            'Updated Date:',
            currentTariffLine?.updated_date
              ? new Date(currentTariffLine.updated_date).toLocaleString()
              : 'N/A'
          )}
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button variant="contained" color="secondary" onClick={toggleEdit}>
            Edit
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={() => setShowConfirmation(true)}
          >
            Delete Line
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      {isLoading ? (
        <Box sx={{ p: 2 }}>
          <Loader />
        </Box>
      ) : (
        renderForm()
      )}
      <Dialog
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
      >
        <DialogTitle variant="h2">Delete Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you would like to delete this tariff line?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowConfirmation(false)}
            variant="outlined"
            color="secondary"
          >
            No
          </Button>
          <Button onClick={handleDelete} color="secondary" variant="contained">
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
